import { useTranslation } from "react-i18next";
import { formatDate, formatNumber } from "../../i18n/format";
import type { BillingUsage } from "../../services/billing/generated/model";
import "./UsageSummary.css";

type Metric = "requests" | "matches" | "activeDeals";
const METRICS: Metric[] = ["requests", "matches", "activeDeals"];

/**
 * Plan, credit balance and usage this month vs the plan's limits (WP2.4).
 * Shared by the company "Plan & usage" page and the moderator company view.
 */
const UsageSummary = ({ usage }: { usage: BillingUsage }) => {
  const { t } = useTranslation("components");
  const month = formatDate(usage.periodStart, { month: "long", year: "numeric" });

  return (
    <div className="usage-summary">
      <div className="usage-summary__tiles">
        <div className="usage-summary__tile">
          <span className="usage-summary__label">{t("usageSummary.currentPlan")}</span>
          <span className="usage-summary__value">{usage.plan.name}</span>
          <span className="usage-summary__hint">
            {usage.plan.isDefault ? t("usageSummary.defaultPlan") : null}
            {usage.plan.usesCredits ? ` ${t("usageSummary.creditBased")}` : null}
          </span>
        </div>
        <div className="usage-summary__tile">
          <span className="usage-summary__label">{t("usageSummary.creditBalance")}</span>
          <span className="usage-summary__value">{formatNumber(usage.creditBalance)}</span>
          <span className="usage-summary__hint">{t("usageSummary.creditMeaning")}</span>
        </div>
        <div className="usage-summary__tile">
          <span className="usage-summary__label">{t("usageSummary.billingCurrency")}</span>
          <span className="usage-summary__value">
            {usage.billingCurrency || t("usageSummary.billingCurrencyUnset")}
          </span>
          <span className="usage-summary__hint">{t("usageSummary.billingCurrencyHint")}</span>
        </div>
      </div>

      <table className="usage-summary__table">
        <caption>{t("usageSummary.period", { month })}</caption>
        <thead>
          <tr>
            <th scope="col">{t("usageSummary.table.metric")}</th>
            <th scope="col">{t("usageSummary.table.used")}</th>
            <th scope="col">{t("usageSummary.table.limit")}</th>
          </tr>
        </thead>
        <tbody>
          {METRICS.map((metric) => {
            const limit = usage.limits[metric];
            const used = usage.usage[metric];
            const atLimit = limit != null && used >= limit;
            return (
              <tr key={metric} className={atLimit ? "usage-summary__row--limit" : undefined}>
                <th scope="row">{t(`usageSummary.metrics.${metric}`)}</th>
                <td>{formatNumber(used)}</td>
                <td>{limit == null ? t("usageSummary.unlimited") : formatNumber(limit)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default UsageSummary;
